import Button from '@ben/orion/button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { VisuallyHidden } from '@mantine/core';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

export interface NomineePickerRow {
  id: string;
  name: string;
  relationshipLabel: string;
}

export interface NomineePickerProps {
  label: string;
  rows: NomineePickerRow[];
  disabled?: boolean;
  onAdd: (id: string) => void;
}

// One labelled section of people who can be nominated with a click.
const NomineePicker = ({
  label,
  rows,
  disabled,
  onAdd,
}: NomineePickerProps) => {
  const { t } = useTranslation();
  const labelId = useId();

  return (
    <Section>
      <Typography id={labelId} variant={TypographyVariant.labelLg}>
        {label}
      </Typography>
      <List aria-labelledby={labelId}>
        {rows.map((row) => (
          <Row key={row.id}>
            <div>
              <Typography variant={TypographyVariant.bodyLg2}>
                {row.name}
              </Typography>
              <RelationshipLabel variant={TypographyVariant.bodyMd1}>
                {row.relationshipLabel}
              </RelationshipLabel>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => onAdd(row.id)}
            >
              {t('benefit.beneficiariesAddDependantButton')}
              <VisuallyHidden> {row.name}</VisuallyHidden>
            </Button>
          </Row>
        ))}
      </List>
    </Section>
  );
};

export default NomineePicker;

const Section = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    width: 100%;
  `}
`;

const List = styled.ul`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    margin: 0;
    padding: 0;
    list-style: none;
  `}
`;

const Row = styled.li`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1.25)};
    padding: ${theme.spacing(1.5, 2)};
    background-color: ${theme.palette.neutral['00']};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: ${theme.borderRadius.sm};
  `}
`;

const RelationshipLabel = styled(Typography)`
  ${({ theme }) => css`
    color: ${theme.palette.neutral[80]};
  `}
`;
